import React, { type ReactNode, useMemo, useReducer } from 'react'
import uuidv4 from 'uuid/v4'

import { ToastItems } from './Toast'
import { ToastContext, type ToastContextType } from './ToastContext'
import type {
  Action,
  HorizontalAlignment,
  State,
  VerticalAlignment,
} from './toastTypes'

const createReducer = (defaultDuration: number) =>
  function reducer(state: State, action: Action): State {
    if (action.type === 'ADD') {
      const { content, id, options } = action
      return [
        ...state,
        {
          action: null,
          content,
          duration: defaultDuration,
          id,
          role: options && options.variant === 'error' ? 'alert' : 'status',
          variant: 'default',
          ...options,
        },
      ]
    }

    if (action.type === 'REMOVE') {
      return state.filter(entry => entry.id !== action.id)
    }

    return state
  }

export type Props = {
  /**
   * The horizontal alignment to use for all toasts within this `ToastProvider`.
   */
  horizontalAlignment?: HorizontalAlignment

  /**
   * The vertical alignment to use for all toasts within this `ToastProvider`.
   */
  verticalAlignment?: VerticalAlignment

  /**
   * The React subtree for which you want Toasts to have the current alignment and duration.
   */
  children: ReactNode

  /**
   * The number of milliseconds that a toast rendered inside of this component will
   * persist by default. You can pass a different `duration` to `addToast` to override
   * this value.
   */
  defaultDuration: number
}

export function ToastProvider({
  children,
  defaultDuration,
  horizontalAlignment = 'center',
  verticalAlignment = 'bottom',
}: Props) {
  const [entries, dispatch] = useReducer(createReducer(defaultDuration), [])

  const value: ToastContextType = useMemo(
    () => ({
      add: (content, options) => {
        const id = uuidv4()
        dispatch({ content, id, options, type: 'ADD' })
        return id
      },
      horizontalAlignment,
      remove: id => {
        dispatch({ id, type: 'REMOVE' })
      },
      verticalAlignment,
    }),
    [horizontalAlignment, verticalAlignment]
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastItems entries={entries} remove={value.remove} />
    </ToastContext.Provider>
  )
}
